import {
  DepressionContentPage,
  FAQItem,
} from "@/components/DepressionContentPage";
import { Link } from "@/lib/router-compat";
import { RelatedLink } from "@/components/RelatedPages";
import { SaintComparisonStatBand } from "@/components/SaintComparisonInline";

const faqs: FAQItem[] = [
  {
    question: "What does treatment-resistant depression mean?",
    answer:
      "Treatment-resistant depression is a clinical term for major depression that has not lifted after at least two adequate trials of antidepressant medication, at a therapeutic dose, for a reasonable length of time. It does not mean you are a difficult patient or that you didn't try hard enough — it means the standard first-line tools didn't work for you.",
  },
  {
    question: "How many people don't respond to antidepressants?",
    answer:
      "Roughly one in three people with depression do not reach remission even after several medication attempts. You are not an outlier; you are in a well-documented group that may benefit from a different approach.",
  },
  {
    question: "Do I have to stop my current medication to try SAINT?",
    answer:
      "No. In most cases, patients continue their existing medication regimen during SAINT treatment. SAINT is not a replacement for what you've built — it is a different kind of intervention aimed at the brain circuitry your medications may not have reached. Never change medication without guidance from your prescribing provider.",
  },
  {
    question: "Is SAINT the same as regular TMS?",
    answer:
      "No. Standard TMS takes six to nine weeks of daily visits and targets the brain using external head measurements. SAINT is an FDA-cleared, accelerated protocol that uses an fMRI to map your individual brain circuitry and compresses treatment into five consecutive days at a Verified SAINT Provider (view our certified provider page at /find-a-verified-saint-provider).",
  },
  {
    question: "What if SAINT isn't appropriate for me?",
    answer:
      "A responsible evaluation may determine that another issue should be addressed first or that SAINT is not the right fit. If that is the case, we will tell you honestly and discuss what other options may be appropriate.",
  },
];

const related: RelatedLink[] = [
  {
    title: "Why Am I So Tired All the Time? Depression Fatigue",
    href: "/learn/always-tired-depression-fatigue",
    description: "How depression drains energy and why rest doesn't fix it.",
  },
  {
    title: "Can Depression Cause Physical Pain?",
    href: "/learn/can-depression-cause-physical-pain",
    description: "Why depression hurts and what helps.",
  },
  {
    title: "High-Functioning Depression Symptoms: 10 Quiet Signs",
    href: "/high-functioning-depression-symptoms-treatment",
    description: "Everyday habits that can be quiet signs of depression.",
  },
  {
    title: "What Is Treatment-Resistant Depression?",
    href: "/what-is-treatment-resistant-depression",
    description:
      "A clear explanation of the term and when it may apply to your experience.",
  },
  {
    title: "What Is SAINT TMS Therapy?",
    href: "/learn/what-is-saint-tms",
    description: "How the five-day, fMRI-guided protocol works.",
  },
];

export default function YouveTriedEverything() {
  return (
    <DepressionContentPage
      seo={{
        title:
          "You've Tried Everything. Why Is the Depression Still Here? | Neuro Wellness",
        description:
          "If antidepressants haven't worked, you may have treatment-resistant depression. Learn why medications fail some people and how SAINT TMS targets the brain circuitry they miss.",
        canonical: "/youve-tried-everything-why-is-the-depression-still-here",
        keywords:
          "treatment-resistant depression, antidepressants not working, SAINT TMS, accelerated TMS, depression still here, tried everything",
      }}
      badge="Patient Story"
      heroTitle="You've Tried Everything. Why Is the Depression Still Here?"
      heroSubtitle="If you have a drawer full of old pill bottles and the same heaviness you had two years ago, there is a name for what you're experiencing — and there is a different kind of treatment built for exactly this situation."
      reviewedBy={undefined}
      lastUpdated="2026-11-03"
      sections={[
        {
          heading: "What 'treatment-resistant' actually means",
          body: (
            <div className="space-y-4">
              <p>
                If you're reading this, there's a decent chance you have a
                drawer somewhere with old pill bottles in it. Maybe an SSRI you
                took for a year that helped a little, then stopped helping.
                Maybe bupropion, which made you jittery and did nothing for your
                mood. Maybe a sleep aid that got you to sleep but left you
                foggy, and something "as needed" for the days when the anxiety
                got loud.
              </p>
              <p>
                You did what you were told. You gave each one a fair shot. And
                you are still waking up with the same heaviness you had two
                years ago.
              </p>

              {/* Photo: You've tried everything. So why is the depression still here? */}
              <div className="my-6 overflow-hidden rounded-2xl border border-border bg-card shadow-soft not-prose">
                <img
                  src="https://vibe.filesafe.space/1787327131284421342/attachments/aee7dd9e-d6b9-4d54-8876-5526f5239533.png"
                  alt="You've tried everything: So why is the depression still here? Sometimes the next step is finding what may have been missed"
                  className="w-full h-auto object-cover"
                  loading="lazy"
                />
              </div>

              <p>
                That experience has a clinical name, and it matters that you
                know it: <strong>treatment-resistant depression</strong>.
              </p>
              <p>
                The definition most clinicians use is simple. If you've had at
                least two adequate trials of antidepressant medication, at a
                therapeutic dose, for a reasonable length of time, and your
                depression hasn't lifted, your depression is considered
                treatment-resistant.
              </p>

              {/* Infographic: What depression can look like when it stays */}
              <div className="my-6 overflow-hidden rounded-2xl border border-border bg-card shadow-soft not-prose">
                <img
                  src="https://vibe.filesafe.space/1787327131284421342/attachments/d771b22d-e24b-4b59-8bfe-5e28de3aeb65.png"
                  alt="What depression can look like when it stays: Low mood, low energy, loss of interest, trouble concentrating, sleep changes, irritability or emotional numbness"
                  className="w-full h-auto object-cover"
                  loading="lazy"
                />
              </div>

              <p>
                Notice what that definition does not say. It doesn't say you're
                a difficult patient. It doesn't say you didn't try hard enough,
                or that you have some unusual, hopeless form of the illness. It
                says the standard first-line tools didn't work for you, which is
                true for a large share of people with major depression. Roughly
                one in three people with depression don't reach remission even
                after several medication attempts.
              </p>
              <p>
                You are not an outlier. You are in a well-documented group that
                simply needs a different approach.
              </p>
            </div>
          ),
        },
        {
          heading: "Why medications fail for some people",
          body: (
            <div className="space-y-4">
              <p>
                Antidepressants work by adjusting the chemistry between neurons,
                mostly serotonin, norepinephrine, or dopamine. For many people
                that's enough. But depression isn't only a chemistry problem.
                It's also a circuitry problem.
              </p>

              {/* Infographic: Why depression can still linger */}
              <div className="my-6 overflow-hidden rounded-2xl border border-border bg-card shadow-soft not-prose">
                <img
                  src="https://vibe.filesafe.space/1787327131284421342/attachments/55ec5684-44e4-479b-9645-6ed62e9b0580.png"
                  alt="Why depression can still linger: Treatment mismatch, overlapping conditions, brain-based factors, life stress and sleep, not fully reviewed"
                  className="w-full h-auto object-cover"
                  loading="lazy"
                />
              </div>

              <p>
                Brain imaging over the last two decades has shown that in
                depression, certain networks become underactive, particularly
                the region behind the forehead responsible for regulating mood,
                motivation, and attention, while other networks involved in
                rumination and self-criticism become overactive. Medication
                changes the chemical environment those circuits sit in, but it
                doesn't directly retrain the circuits themselves.
              </p>
              <p>
                That's part of why someone can be on a high dose of a good
                medication, take it faithfully, and still feel like nothing has
                fundamentally changed. The signal isn't getting through.
              </p>
            </div>
          ),
        },
        {
          heading: "The hidden cost of 'just try one more'",
          body: (
            <div className="space-y-4">
              <p>
                There's another reason to name treatment resistance early: every
                failed trial has a price. Each new medication takes weeks to
                build up and weeks to taper off. Each one brings its own side
                effects — dry mouth, headaches, digestive problems, blunted
                emotions, weight changes. And each one that fails chips away at
                something harder to measure: your belief that anything will ever
                work.
              </p>
              <p>
                Many people arrive at a TMS consultation not because a doctor
                referred them, but because they got tired. Tired of adjusting
                doses. Tired of being told the next one might be different. If
                that's you, that fatigue isn't a character flaw. It's
                information. It's telling you that repeating the same category
                of treatment is not a plan.
              </p>
            </div>
          ),
        },
        {
          heading: "When it's time to consider something beyond medication",
          body: (
            <div className="space-y-4">
              <p>
                Here are some signs that you may be a candidate for a
                non-medication treatment like transcranial magnetic stimulation
                (TMS):
              </p>
              <ul className="list-disc pl-6 space-y-2 marker:text-accent">
                <li>
                  You've tried two or more antidepressants without adequate
                  relief
                </li>
                <li>
                  You've had partial improvement but still feel like you're
                  operating at half capacity
                </li>
                <li>
                  Side effects have made it hard to stay on the medications that
                  did help a little
                </li>
                <li>
                  Your depression keeps coming back after periods of improvement
                </li>
                <li>
                  You're doing therapy and taking your medication and still feel
                  stuck
                </li>
              </ul>

              {/* Photo: A fresh conversation can reveal what may have been missed */}
              <div className="my-6 overflow-hidden rounded-2xl border border-border bg-card shadow-soft not-prose">
                <img
                  src="https://vibe.filesafe.space/1787327131284421342/attachments/66ad796f-8fbb-4e65-b9c3-89c2fea77195.png"
                  alt="A fresh conversation can reveal what may have been missed: clinician in warm consultation with patient"
                  className="w-full h-auto object-cover"
                  loading="lazy"
                />
              </div>

              <p>
                You don't have to stop your current medications to pursue TMS.
                In most cases, patients continue their existing regimen during
                treatment. TMS isn't a replacement for what you've built; it's
                an addition that works on a different level.
              </p>

              {/* Infographic: What the next step can look like */}
              <div className="my-6 overflow-hidden rounded-2xl border border-border bg-card shadow-soft not-prose">
                <img
                  src="https://vibe.filesafe.space/1787327131284421342/attachments/ec2d2389-2841-4b93-97d5-b7048c72cb6a.png"
                  alt="What the next step can look like: 1. Evaluation, 2. Therapy or medication review, 3. TMS options, 4. SAINT when appropriate, 5. Follow-up care"
                  className="w-full h-auto object-cover"
                  loading="lazy"
                />
              </div>
            </div>
          ),
        },
        {
          heading: "What SAINT does differently",
          body: (
            <div className="space-y-4">
              <div className="my-6 grid gap-4 sm:grid-cols-2 not-prose">
                <div className="overflow-hidden rounded-xl border border-border bg-card shadow-soft">
                  <div className="relative aspect-[4/3] w-full overflow-hidden bg-muted">
                    <img
                      src="https://vibe.filesafe.space/1787327131284421342/attachments/61fd8a15-3767-464e-99b6-5a1351dea1b3.png"
                      alt="Personalized fMRI brain scan targeting for SAINT® TMS therapy"
                      className="h-full w-full object-cover"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-3.5">
                    <p className="font-headings text-sm font-bold text-primary">
                      Step 2 &amp; 3 · Personalized fMRI Targeting
                    </p>
                    <p className="mt-1 text-xs text-muted-foreground leading-relaxed">
                      A resting-state fMRI maps your individual brain circuitry
                      before treatment begins.
                    </p>
                  </div>
                </div>

                <div className="overflow-hidden rounded-xl border border-border bg-card shadow-soft">
                  <div className="relative aspect-[4/3] w-full overflow-hidden bg-muted">
                    <img
                      src="https://vibe.filesafe.space/1787327131284421342/attachments/9907b0c5-c62d-4096-ba03-9d7a9cc3ffc5.png"
                      alt="Patient seated comfortably in SAINT® treatment suite during stimulation series"
                      className="h-full w-full object-cover"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-3.5">
                    <p className="font-headings text-sm font-bold text-primary">
                      Step 4 · 5-Day Stimulation Series
                    </p>
                    <p className="mt-1 text-xs text-muted-foreground leading-relaxed">
                      Ten 10-minute sessions per day with 50-minute breaks;
                      awake and alert throughout.
                    </p>
                  </div>
                </div>
              </div>

              <p>
                Standard TMS uses focused magnetic pulses, delivered through a
                coil placed against the scalp, to stimulate the underactive
                mood-regulation region of the brain directly. There's no
                anesthesia and no systemic medication. It works, but it takes
                six to nine weeks of daily visits, and it locates the target
                using external measurements of the head rather than a picture of
                your actual brain.
              </p>
              <p>
                SAINT (Stanford Accelerated Intelligent Neuromodulation Therapy)
                is the accelerated, FDA-cleared evolution of that approach. It
                was developed at Stanford's Brain Stimulation Lab and is
                indicated specifically for adults with major depressive disorder
                who haven't gotten adequate relief from antidepressant
                medication in their current episode. In other words, it was
                built for the person described at the top of this article.
              </p>
              <p>
                Here is how the process works at a{" "}
                <Link
                  to="/find-a-verified-saint-provider"
                  className="font-bold text-accent underline underline-offset-2 hover:text-accent/80"
                >
                  Verified SAINT® Provider
                </Link>{" "}
                like Neuro Wellness TMS Centers of America:
              </p>
              <ol className="list-decimal pl-6 space-y-3 marker:text-accent marker:font-bold">
                <li>
                  <strong>Consultation (Zoom web conference).</strong> You meet
                  with our clinical team over a convenient Zoom call. As your
                  evaluation evolves, you will also have the option to meet with
                  our psychiatrist by Zoom web conference or in person at our
                  clinic. You meet with our clinical team to review your
                  history, your medication trials, and your current symptoms,
                  and to confirm you're a candidate.
                </li>
                <li>
                  <strong>fMRI brain scan (mandatory in person).</strong> An
                  fMRI, taking roughly 45 minutes, maps your brain's individual
                  circuitry. This is the step that separates SAINT from every
                  other depression treatment: no one else's scan is used to plan
                  your care. (Mandatory in-person scan at an imaging partner
                  center.)
                </li>
                <li>
                  <strong>Personalized targeting.</strong> A targeting algorithm
                  analyzes your scan to identify the precise spot where
                  stimulation will most effectively reach the depression
                  network. Neuronavigation then guides the coil to that exact
                  location for every session.
                </li>
                <li>
                  <strong>
                    Five-day stimulation series (mandatory in person).
                  </strong>{" "}
                  In-person treatment Monday through Friday at our Coral Springs
                  clinic. Treatment is ten 10-minute sessions per day, with
                  50-minute breaks in between, for five consecutive days. That's
                  five clinic visits instead of the 30 to 36 required for
                  conventional TMS. Most patients begin noticing a shift in mood
                  within the first couple of days.
                </li>
              </ol>

              {/* Dedicated Private Suites Callout */}
              <div className="overflow-hidden rounded-xl border border-border bg-card shadow-soft not-prose my-6">
                <div className="relative aspect-[16/9] w-full overflow-hidden bg-muted">
                  <img
                    src="https://vibe.filesafe.space/1787327131284421342/attachments/bb78020b-18d0-4701-b491-b8867ddad417.png"
                    alt="Private executive suite in the SAINT Lounge Wing with blue sectional, power recliner, desk, and refreshment bar"
                    className="h-full w-full object-cover"
                    loading="lazy"
                  />
                </div>
                <div className="p-4">
                  <p className="font-headings text-sm font-bold text-primary">
                    Comfort, Privacy &amp; Dedicated SAINT® Lounge Wing
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground leading-relaxed">
                    Between sessions, you relax in a private furnished suite
                    with power recliners, smart TV, quiet workstations, and
                    refreshments. Our on-site care team checks in throughout the
                    day to ensure you have lunch in peace and stay completely
                    comfortable. Companions and family members are warmly
                    welcome.
                  </p>
                </div>
              </div>
            </div>
          ),
        },
        {
          heading: "What the results look like",
          body: (
            <div className="space-y-4">
              <SaintComparisonStatBand />
              <p>
                In the published clinical trials behind SAINT's FDA clearance,
                participants had treatment-resistant depression and had already
                failed prior treatments. Seventy-nine percent reached remission,
                not merely improvement, and the average time to remission was
                about two and a half days. Across more than 400 patients treated
                in trials, 85 percent experienced symptom relief, with no
                serious adverse events.
              </p>
              <p>
                The most common side effects were mild headache, fatigue, and
                temporary scalp discomfort at the treatment site, which
                typically resolve on their own. SAINT isn't appropriate for
                everyone: people with non-removable metal in or near the head or
                an implanted medical device aren't eligible, and your provider
                will screen for these at the consultation.
              </p>
              <p>
                You don't have to stop your current medications to pursue SAINT.
                Most patients continue their existing regimen through treatment.
                It isn't a replacement for what you've built; it's a different
                kind of intervention aimed at the circuitry your medications
                have never been able to reach.
              </p>
            </div>
          ),
        },
        {
          heading: "What to do next",
          variant: "callout",
          callout:
            "If you recognized yourself in the first paragraph of this article, you've already done the hard part: you've kept going. The next step is smaller than you think. At Neuro Wellness TMS Centers of America, the first appointment is an evaluation, not a commitment. We'll review your medication history, your current symptoms, and your goals, and we'll tell you honestly whether SAINT is a good fit. If it is, we'll walk you through the scan, the schedule, and what to expect during your treatment week. If it isn't, we'll tell you that too. Schedule a consultation: 954-827-9793.",
        },
      ]}
      faq={faqs}
      faqHeading="Questions About Treatment-Resistant Depression & SAINT"
      relatedLinks={related}
      finalCtaHeading="You've Kept Going. Let's Find What's Next."
      finalCtaText="If antidepressants haven't worked, there is a different kind of treatment built for exactly this situation. Speak with our team about whether SAINT may be appropriate for you."
      finalCtaPrimaryLabel="Schedule a Free Consultation"
      finalCtaPrimaryHref="/saint"
      finalCtaSecondaryLabel="Call 954-827-9793"
      finalCtaSecondaryHref="tel:9548279793"
    />
  );
}
